import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { getColorTheme, getActiveThemeColorSet } from '@/utils/storage';
import { RebuildBlockLogo } from './RebuildBlockLogo';

const SPLASH_MESSAGE = 'Small steps. Daily momentum.';
const WORD_DELAY_MS = 140;
const AFTER_STREAM_MS = 550;

const SimpleSplash = ({ onComplete }) => {
  const initialTheme = getActiveThemeColorSet();
  const [currentTheme, setCurrentTheme] = useState(initialTheme);
  const [phase, setPhase] = useState('wait');
  const [wordCount, setWordCount] = useState(0);

  const words = useMemo(() => SPLASH_MESSAGE.split(/\s+/).filter(Boolean), []);

  useEffect(() => {
    setCurrentTheme(getActiveThemeColorSet());
    console.log('🎨 Splash screen using theme:', getColorTheme());
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setPhase('stream'), 1000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (phase !== 'stream') return;
    if (wordCount >= words.length) {
      const t = setTimeout(() => onComplete(), AFTER_STREAM_MS);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setWordCount((n) => n + 1), WORD_DELAY_MS);
    return () => clearTimeout(t);
  }, [phase, wordCount, words.length, onComplete, words]);

  useLayoutEffect(() => {
    const savedTheme = getColorTheme();
    const theme = getActiveThemeColorSet();
    const root = document.documentElement;

    root.style.setProperty('--theme-primary', theme.primary);
    root.style.setProperty('--theme-secondary', theme.secondary);
    root.style.setProperty('--theme-accent', theme.accent);
    root.style.setProperty('--theme-light', theme.light);
    root.style.setProperty('--theme-btn-bg', theme.primary);
    root.style.setProperty('--theme-btn-hover', theme.secondary);
    root.style.setProperty('--theme-border', theme.secondary);
    root.style.setProperty('--theme-input-bg', `${theme.light}80`);
    root.style.setProperty('--theme-text-primary', theme.textPrimary || '#2D2D35');
    root.style.setProperty('--theme-text-secondary', theme.textSecondary || '#73737D');
    root.style.setProperty('--theme-logo-text', theme.logoText || '#F9F9FF');
    document.body.setAttribute('data-theme', savedTheme);
  }, []);

  const line = words.slice(0, wordCount).join(' ');

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 px-4"
      style={{ backgroundColor: currentTheme.light }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <RebuildBlockLogo className="w-full h-auto" scaleText={1.1} fontWeight="700" />
      </motion.div>
      {phase === 'stream' && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="font-body text-base text-center text-secondary max-w-sm min-h-[1.5rem]"
        >
          {line}
        </motion.p>
      )}
    </motion.div>
  );
};

export default SimpleSplash;
